import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Beer, Wine, Martini, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_IMAGES = {
  beer: "https://images.pexels.com/photos/1269043/pexels-photo-1269043.jpeg?auto=compress&cs=tinysrgb&w=800",
  wine: "https://images.pexels.com/photos/2702805/pexels-photo-2702805.jpeg?auto=compress&cs=tinysrgb&w=800",
  cocktails: "https://images.pexels.com/photos/4051396/pexels-photo-4051396.jpeg?auto=compress&cs=tinysrgb&w=800",
  spirits: "https://images.pexels.com/photos/602750/pexels-photo-602750.jpeg?auto=compress&cs=tinysrgb&w=800"
};

const MenuPage = () => {
  const [menu, setMenu] = useState(null);
  const [activeTab, setActiveTab] = useState("beer");

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API}/menu`);
      setMenu(response.data);
    } catch (error) {
      console.error("Error fetching menu:", error);
      // Fallback data
      setMenu({
        beer: [
          { name: "Estrella", type: "Lager" },
          { name: "Becks", type: "Lager" },
          { name: "Birra Moretti", type: "Lager" },
          { name: "Firebird Bohemia", type: "Pilsner" },
          { name: "Firebird Work Ethic IPA", type: "IPA" }
        ],
        wine: {
          white: [
            { name: "The Ned 'Waihopai River' Sauvignon Blanc 2022", region: "Marlborough" },
            { name: "Nals Margreid 'Stella Alpina' Pinot Grigio 2022", region: "Alto Adige DOC" }
          ],
          red: [
            { name: "Château Méaume Bordeaux Supérieur 2020", region: "Bordeaux" },
            { name: "Cune Rioja Reserva 2019", region: "Rioja" }
          ],
          rose: [
            { name: "Peyrassol 'Les Templiers' Organic Rosé 2022", region: "Côtes de Provence" },
            { name: "Jas des Vignes Rosé 2022", region: "IGP Méditerranée" }
          ],
          bubbles: [
            { name: "Cune Cava Brut", region: "Penedès" },
            { name: "La Gioiosa Prosecco DOC Rosé 2022", region: "Italy" }
          ]
        },
        cocktails: [
          { name: "Aperol Spritz" },
          { name: "Pimms" },
          { name: "Moscow Mule" },
          { name: "Espresso Martini" },
          { name: "Gin and Tonic" }
        ],
        spirits: [
          { name: "Vodka", brands: ["Smirnoff"] },
          { name: "Gin", brands: ["Bombay Sapphire"] },
          { name: "Rum", brands: ["Bacardi"] }
        ]
      });
    }
  };

  const tabs = [
    { id: "beer", label: "Beer", icon: <Beer className="w-5 h-5" /> },
    { id: "wine", label: "Wine", icon: <Wine className="w-5 h-5" /> },
    { id: "cocktails", label: "Cocktails", icon: <Martini className="w-5 h-5" /> },
    { id: "spirits", label: "Spirits", icon: <Wine className="w-5 h-5" /> }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="menu-page">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#050505]">
        <div className="container-custom text-center">
          <p className="label-text text-yellow-400 mb-4">Our Selection</p>
          <h1 className="heading-xl text-white mb-6">Menu</h1>
          <p className="text-body max-w-2xl mx-auto">
            Premium draught beers, fine wines, signature cocktails, and quality spirits. 
            All included in our packages or available as add-ons.
          </p>
        </div>
      </section>

      {/* Menu Tabs */}
      <section className="pb-24 bg-[#050505]" data-testid="menu-section">
        <div className="container-custom">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 font-semibold uppercase tracking-wider text-sm transition-all ${
                  activeTab === tab.id
                    ? 'bg-yellow-400 text-black'
                    : 'bg-white/5 text-white hover:bg-white/10'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {menu && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={MENU_IMAGES[activeTab]}
                  alt={activeTab}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="surface-card p-8" data-testid="menu-content">
                {/* Beer */}
                {activeTab === "beer" && (
                  <div>
                    <h2 className="heading-lg text-white mb-6">Draught Beers</h2>
                    <p className="text-body mb-8">
                      Refreshing beers on tap, perfect for any celebration. 
                      Available in 30L and 50L kegs.
                    </p>
                    <div className="space-y-4">
                      {menu.beer.map((item, index) => (
                        <div 
                          key={index} 
                          className="flex justify-between items-center py-3 border-b border-white/10"
                          data-testid={`beer-item-${index}`}
                        >
                          <span className="text-white font-semibold">{item.name}</span>
                          <span className="text-neutral-500 text-sm">{item.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Wine */}
                {activeTab === "wine" && (
                  <div>
                    <h2 className="heading-lg text-white mb-6">Wine Selection</h2>
                    <p className="text-body mb-8">
                      Carefully curated wines from renowned regions. 
                      Available by the bottle.
                    </p>
                    
                    {Object.entries(menu.wine).map(([category, wines]) => (
                      <div key={category} className="mb-8">
                        <h3 className="text-yellow-400 uppercase tracking-widest text-sm font-bold mb-4">
                          {category === "bubbles" ? "Sparkling" : category}
                        </h3>
                        <div className="space-y-3">
                          {wines.map((wine, index) => (
                            <div 
                              key={index}
                              className="py-2 border-b border-white/5"
                              data-testid={`wine-${category}-${index}`}
                            >
                              <p className="text-white">{wine.name}</p>
                              <p className="text-neutral-500 text-sm">{wine.region}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Cocktails */}
                {activeTab === "cocktails" && (
                  <div>
                    <h2 className="heading-lg text-white mb-6">Signature Cocktails</h2>
                    <p className="text-body mb-8">
                      Classic cocktails crafted by our skilled bartenders. 
                      Available as an add-on to any package.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {menu.cocktails.map((cocktail, index) => (
                        <div 
                          key={index}
                          className="bg-white/5 p-4 text-center"
                          data-testid={`cocktail-item-${index}`}
                        >
                          <span className="text-white font-semibold">{cocktail.name}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-neutral-500 text-sm mt-6 text-center">
                      Enquire for other cocktail options
                    </p>
                  </div>
                )}

                {/* Spirits */}
                {activeTab === "spirits" && (
                  <div>
                    <h2 className="heading-lg text-white mb-6">Spirits & Mixers</h2>
                    <p className="text-body mb-8">
                      Premium spirits with a full range of mixers. 
                      Available as an add-on to any package.
                    </p>
                    <div className="space-y-4">
                      {menu.spirits.map((spirit, index) => (
                        <div 
                          key={index}
                          className="py-3 border-b border-white/10"
                          data-testid={`spirit-item-${index}`}
                        >
                          <p className="text-white font-semibold mb-1">{spirit.name}</p>
                          <p className="text-neutral-500 text-sm">
                            {spirit.brands.join(" • ")}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <h3 className="heading-md text-white mb-4">Ready to Book?</h3>
          <p className="text-body mb-8 max-w-xl mx-auto">
            View our packages and customize your drinks selection for your perfect event.
          </p>
          <Link to="/pricing" className="btn-primary" data-testid="menu-view-packages">
            View Packages
            <ChevronRight className="inline ml-2 w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MenuPage;
